/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Stack,
  TextField,
  Alert,
  AlertTitle,
  Typography,
  Button,
} from '@mui/material';
import {
  LoadingButton,
} from '@mui/lab';
import { zodResolver } from '@hookform/resolvers/zod';
// eslint-disable-next-line import/no-extraneous-dependencies
import * as z from 'zod';
import {
  useForm,
  getFieldProps,
  getSubmitButtonProps,
  getFormProps,
  InputPhone,
  Validate,
  CountrySelector,
  TimezoneSelector,
} from '@marageti/z4-lib';
import {
  Controller,
} from 'react-hook-form';
import { Phone, TimeZone } from '@marageti/z4-sdk/lib/standards';

type TravelerFormDefaultValues = {
  givenName: string | undefined,
  familyName: string | undefined,
  phone: Phone | undefined,
  timeZone: string | undefined,
};

const DEFAULT_VALUES = {
  givenName: '',
  familyName: '',
  phone: {
    prefix: '',
    number: '',
  },
  countryOfResidence: undefined,
  timeZone: '',
} as TravelerFormDefaultValues;

const schema = z.object({
  givenName: z.string().min(1, 'Required'),
  familyName: z.string().min(1, 'Required'),
  phone: z.object({
    prefix: z.string(),
    number: z.string(),
  }).refine((value) => {
    if (!value.number) return true;
    return Validate.phone(`${value.prefix}${value.number}`);
  }),
  countryOfResidence: z.string().nullable(),
  timeZone: z.object({
    name: z.string(),
    rawOffsetInMinutes: z.any(),
  })
    .refine((data) => data.name && data.rawOffsetInMinutes !== undefined, {
      message: 'Required',
      path: [],
    }),
});

type TravelerFormProps = {
  defaultValues: {
    givenName: string | undefined,
    familyName: string | undefined,
    phone: Phone,
    countryOfResidence: string | undefined,
    timeZone: TimeZone,
  },
  onSubmit: (data: any) => void,
  submissionError: boolean
  onCancel?: () => void
};

const TravelerForm = ({
  defaultValues,
  onSubmit,
  submissionError,
  onCancel = undefined,
}: TravelerFormProps) => {
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
    control,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      ...DEFAULT_VALUES,
      ...defaultValues,
    },
  });

  return (
    <form {...getFormProps(handleSubmit(onSubmit))}>
      <Stack spacing={2}>
        <Stack useFlexGap>
          <Typography
            variant="h3"
            sx={{
              mb: 1,
            }}
          >
            Edit Profile
          </Typography>
          <TextField
            label="First Name"
            {...register('givenName')}
            {...getFieldProps('givenName', errors)}
            required
          />
          <TextField
            label="Last Name"
            {...register('familyName')}
            {...getFieldProps('familyName', errors)}
            required
          />
          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <InputPhone
                id="phone"
                label="Phone Number"
                {...getFieldProps('phone', errors)}
                {...field}
              />
            )}
          />
          <Controller
            name="countryOfResidence"
            control={control}
            render={({ field }) => (
              <CountrySelector
                {...field}
                {...getFieldProps('countryOfResidence', errors)}
                label="Country of Residence"
                onChange={(_: any, newValue: any) => {
                  field.onChange(newValue?.value || null);
                }}
              />
            )}
          />

          <Controller
            name="timeZone"
            control={control}
            render={({ field }) => (
              <TimezoneSelector
                required
                id="timeZone-selector"
                label="Timezone"
                {...getFieldProps('timeZone', errors)}
                {...field}
              />
            )}
          />
        </Stack>

        {submissionError && (
          <Alert severity="error">
            <AlertTitle>There was an error submitting the form.</AlertTitle>
            Please try again.
          </Alert>
        )}

        <Stack
          direction="row"
          spacing={2}
          sx={(theme) => ({
            [theme.breakpoints.down('desktop')]: {
              marginTop: 'auto',
              flexDirection: 'column-reverse',
              '& > :not(:first-of-type)': {
                margin: theme.spacing(0, 0, 2.5, 0),
              },
            },
          })}
        >
          <LoadingButton
            variant="contained"
            type="submit"
            {...getSubmitButtonProps(isSubmitting, isValid)}
          >
            Save
          </LoadingButton>
          <Button
            variant="outlined"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};

export default TravelerForm;
